import {
  Component,
  Input,
  Output,
  EventEmitter,
  CUSTOM_ELEMENTS_SCHEMA,
  forwardRef,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

import type { LeafField } from '@cigna/leaf-web/lib/wc';
import '@cigna/leaf-web/lib/wc/components/field/field';

@Component({
  selector: 'cigna-leaf-field',
  template: `
    <leaf-field
      [autocomplete]="autocomplete"
      [value]="value"
      [pattern]="pattern"
      [type]="type"
      [placeholder]="placeholder"
      [label]="label"
      [name]="name"
      [fieldNote]="fieldNote"
      [ariaLive]="ariaLive"
      [required]="required"
      [isError]="isError"
      [errorText]="errorText"
      [disabled]="disabled"
      [max]="max"
      [min]="min"
      [errorNote]="isError ? errorNote : undefined"
      [maxlength]="maxlength"
      [isSuccess]="isSuccess"
      [successText]="successText"
      [readonly]="readonly"
      [successNote]="isSuccess ? successNote : undefined"
      (blur)="onTouched()"
      (input)="inputHandler($event.target.value)"
    ></leaf-field>
  `,
  standalone: true,
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => CignaLeafFieldComponent),
      multi: true,
    },
  ],
})
export class CignaLeafFieldComponent implements ControlValueAccessor {
  @Input() label: LeafField['label'];
  @Input() type: LeafField['type'] = 'text';

  @Input() ariaLive: LeafField['ariaLive'];
  @Input() autocomplete: LeafField['autocomplete'];
  @Input() errorNote: LeafField['errorNote'];
  @Input() errorText: LeafField['errorText'];
  @Input() disabled: LeafField['disabled'];
  @Input() fieldNote: LeafField['fieldNote'];
  @Input() isError: LeafField['isError'];
  @Input() isSuccess: LeafField['isSuccess'];
  @Input() max: LeafField['max'];
  @Input() maxlength: LeafField['maxlength'];
  @Input() min: LeafField['min'];
  @Input() name: LeafField['name'];
  @Input() pattern: LeafField['pattern'];
  @Input() placeholder: LeafField['placeholder'];
  @Input() readonly: LeafField['readonly'];
  @Input() required: LeafField['required'];
  @Input() successNote: LeafField['successNote'];
  @Input() isOptional: LeafField['isOptional'];
  @Input() successText: LeafField['successText'];

  /**
   * ********
   * CUSTOM ANGULAR PROPERTIES
   */
  @Output() change = new EventEmitter<string>();

  private _value = '';

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onChange: any = () => {};
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onTouched: any = () => {};

  inputHandler(val: string) {
    this._value = val;
    this.onTouched();
    this.onChange(this._value);
    this.change.emit(val);
  }

  @Input()
  get value() {
    return this._value;
  }

  set value(val) {
    if (val !== this._value) {
      this._value = val;
      this.onTouched();
    }
  }

  writeValue(value: string) {
    this.value = value;
  }

  registerOnChange(fn: (value: string) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }
}
